import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  buildTreeFromFields,
  countFormPages,
  countRemainingFormPages,
  isValueEmpty,
  resolveInitialFieldStack,
  resolveNodesToPush,
} from "../../utils";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { FormShallowStoreHook } from "../../store";
import { activeNodeOf, pageIndexOf, popToPreviousPage, type FieldStack } from "./field-stack";
import { useTrackFieldView } from "./use-track-field-view";
import { useTrackFieldProgress } from "../use-form-analytics";

const percentageForPage = (pageIndex: number, totalPages: number) => ((pageIndex + 1) / totalPages) * 100;

export interface UseFormNavigationArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  subFieldIds: Set<string>;
  currentResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue>;
  logicOptions: NextFieldLogicOptions;
  existingResponses: FormResponseValue[] | undefined;
  startingFieldId: string | undefined;
  onBeforeAdvance: (activeField: FormFieldNode) => void;
  responseId: string | undefined;
  responseAccessCode: string | undefined;
  useFormShallowStore: FormShallowStoreHook;
  onTraverse?: (event: FormTraverseEvent) => void;
}

/** What kind of navigation just happened. "initial" fires once, for the field the wizard opens on. */
export type FormTraverseDirection = "initial" | "forward" | "back" | "jump";

/**
 * Fired by {@link useFormNavigation} on every navigation event, including the initial mount.
 *
 * Carries the raw field node rather than a derived "is this blocked" boolean: `field.value.options
 * ?.disableNext` (a static, admin-authored block) and a transient validation error (not present on
 * this event at all — see `is-next-disabled.ts`) are different things a conflated boolean would blur
 * together. Consumers read `field.value.options?.disableNext` and `?.disableGoBack` directly and
 * decide for themselves what combination counts as "blocked" — this package makes no judgment call.
 */
export interface FormTraverseEvent {
  direction: FormTraverseDirection;
  field: FormFieldNode;
  currentPageIndex: number;
  totalPages: number;
}

export const useFormNavigation = ({
  fieldsArr,
  fieldsMap,
  subFieldIds,
  currentResponses,
  responsesMap,
  logicOptions,
  existingResponses,
  startingFieldId,
  onBeforeAdvance,
  responseId,
  responseAccessCode,
  useFormShallowStore,
  onTraverse,
}: UseFormNavigationArgs) => {
  const fieldTree = useMemo(() => {
    if (!fieldsArr?.length) return undefined;
    return buildTreeFromFields(fieldsArr);
  }, [fieldsArr]);

  const hasNoRootField = !!fieldsArr?.length && !fieldTree;

  const existingResponseFieldIds = useMemo(
    () => new Set(existingResponses?.map((r) => r.fieldId) ?? []),
    [existingResponses]
  );

  const walkContext = useMemo(
    () => ({
      responsesByFieldId: currentResponses,
      allResponses: Object.values(currentResponses),
      logicOptions,
    }),
    [currentResponses, logicOptions]
  );

  // `resolveInitialFieldStack` itself is documented as "applied once on mount; not a live projection
  // of the answers" — but recomputing this useMemo on every responsesMap change violated that
  // contract. A field whose answer save resolves asynchronously (Stripe, Chargebee, Appointment
  // Booking — see use-auto-advance.ts) can update responsesMap *before* its own debounced auto-advance
  // timer fires; while storedFieldStack is still undefined (no real navigation has committed yet),
  // that responsesMap change would silently re-resolve this to the *next* field — one hop the enduser
  // never took and nothing tracked (no setFieldStack call, no onTraverse event, no analytics beacon) —
  // and the debounced advance then adds a second, real hop on top of that, overshooting by one field.
  // Freezing this the first time it resolves to a defined value closes that window: from then on, the
  // only way `fieldStack` moves is through a real, tracked navigation call.
  //
  // This freeze is only safe because `fieldsArr`/`fieldsMap` and `responsesMap` are guaranteed to
  // resolve together, atomically, from the one caller this hook has today (`useTellescopeEnduserForm`,
  // which requires a truthy `accessCode` and therefore always takes `use-form-data-queries.ts`'s single
  // `enduserResponseInfoQuery` branch — see that file's own two-flow doc comment). That query's other
  // branch (`formQuery`/`formFieldsQuery`/`responseQuery`, reachable only without an access code) fetches
  // the field tree and the response as two independent queries with no ordering guarantee — if this hook
  // were ever reused against that branch (e.g. `accessCode` made optional), `fieldTree` could resolve
  // and freeze `openingStack` before `responsesMap` reflects a returning enduser's real prior answers,
  // permanently reopening on the wrong field. Revisit this freeze if that ever becomes reachable.
  const openingStackRef = useRef<FieldStack | undefined>(undefined);
  const openingStack = useMemo((): FieldStack | undefined => {
    if (openingStackRef.current) return openingStackRef.current;
    if (!fieldTree) return undefined;

    const resolved = resolveInitialFieldStack({
      fieldTree,
      fieldsMap,
      answeredFieldIds: new Set(Object.keys(responsesMap)),
      existingResponseFieldIds,
      startingFieldId,
      walkContext: {
        ...walkContext,
        allResponses: walkContext.allResponses.filter((r) => !isValueEmpty(r.answer?.value)),
      },
    });
    openingStackRef.current = resolved;
    return resolved;
  }, [fieldTree, fieldsMap, responsesMap, startingFieldId, existingResponseFieldIds, walkContext]);

  const { fieldStack: storedFieldStack, setFieldStack } = useFormShallowStore("fieldStack", "setFieldStack");

  const fieldStack = useMemo(
    (): FieldStack => storedFieldStack ?? openingStack ?? [],
    [storedFieldStack, openingStack]
  );

  const activeField = activeNodeOf(fieldStack);

  const trackFieldView = useTrackFieldView({ responseId, accessCode: responseAccessCode });
  const trackFieldProgress = useTrackFieldProgress();

  const pageIndex = pageIndexOf(fieldStack, subFieldIds, fieldsMap);

  const totalPages = useMemo(() => {
    if (!fieldTree) return 1;
    return countFormPages(fieldTree, subFieldIds, walkContext, fieldsMap);
  }, [fieldTree, walkContext, subFieldIds, fieldsMap]);

  const progress = percentageForPage(pageIndex, totalPages);

  // Single place that computes an event's currentPageIndex (always against the stack the field just
  // landed on, never the pre-navigation one) and calls onTraverse — used by the opening-field effect
  // and all three navigation functions below, so the event shape only needs updating in one place.
  const emitTraverse = useCallback(
    (direction: FormTraverseDirection, field: FormFieldNode, stack: FieldStack) => {
      onTraverse?.({ direction, field, currentPageIndex: pageIndexOf(stack, subFieldIds, fieldsMap), totalPages });
    },
    [onTraverse, subFieldIds, fieldsMap, totalPages]
  );

  // Navigation views are interaction-caused and reported by the handlers below; the opening field is
  // display-caused (nobody navigated to it), which is the one shape react.dev/learn/you-might-not-need-an-effect
  // keeps in an effect — trackFieldView/trackFieldProgress/onTraverse are all parent-supplied callbacks
  // fed data computed here for that same reason. Repeat runs are absorbed by the trackers' own
  // per-field dedupe — but onTraverse has no such tolerance (a consumer reading it as "one event per
  // navigation" would misread a second "initial" as a separate arrival). `storedFieldStack ===
  // undefined` alone isn't a reliable "no navigation has happened yet" signal: a field whose answer
  // save resolves asynchronously and lands before its own auto-advance timer fires (Stripe, Chargebee,
  // Appointment Booking — see use-auto-advance.ts) can recompute `openingStack` onto the *next* field
  // while `storedFieldStack` is still `undefined`, since the actual `setFieldStack` write that would
  // flip that gate hasn't committed yet. A per-field-id dedupe alone doesn't catch this, since the
  // second field differs from the first — so this is a one-shot latch: once "initial" has fired for
  // any field, it never fires again for this hook instance, and every field reached afterward is
  // `goToNextFieldInternal`'s own real "forward" event, at the moment the field-stack write commits.
  const openingField = storedFieldStack === undefined && openingStack ? activeNodeOf(openingStack) : undefined;
  const hasFiredInitialRef = useRef(false);
  // openingField traces back through openingStackRef (an internal one-shot latch, not a DOM/instance
  // ref meant for a parent), which is all this rule can see — it can't tell that apart from the
  // forwardRef-shaped pattern it actually targets.
  /* eslint-disable react-you-might-not-need-an-effect/no-pass-ref-to-parent */
  useEffect(() => {
    if (!openingField) return;
    trackFieldView(openingField.value.id);
    trackFieldProgress(openingField);
    if (hasFiredInitialRef.current) return;
    hasFiredInitialRef.current = true;
    emitTraverse("initial", openingField, openingStack ?? []);
  }, [openingField, trackFieldView, trackFieldProgress, emitTraverse, openingStack]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-ref-to-parent */

  const nextPush = useMemo(() => {
    if (!activeField) return { nodes: [], reachedPage: false };
    return resolveNodesToPush({ activeField, fieldsMap, subFieldIds, walkContext });
  }, [activeField, walkContext, subFieldIds, fieldsMap]);

  // A trailing pass-through run with no page after it is never pushed: the active node must be a
  // page, and legacy left those answers out of the submission too.
  const nextField = nextPush.reachedPage ? nextPush.nodes[nextPush.nodes.length - 1] : undefined;

  const goToNextFieldInternal = useCallback(() => {
    if (!activeField || !nextField) return;

    onBeforeAdvance(activeField);
    const newStack = [...fieldStack, ...nextPush.nodes];
    setFieldStack(newStack);
    trackFieldView(nextField.value.id);
    trackFieldProgress(nextField, activeField);
    emitTraverse("forward", nextField, newStack);
  }, [
    activeField,
    nextField,
    nextPush.nodes,
    onBeforeAdvance,
    fieldStack,
    setFieldStack,
    trackFieldView,
    trackFieldProgress,
    emitTraverse,
  ]);

  const goToPreviousField = useCallback(() => {
    if (!activeField || activeField.value.options?.disableGoBack) return;

    const popped = popToPreviousPage(fieldStack, subFieldIds, fieldsMap);
    const previous = activeNodeOf(popped);
    if (popped === fieldStack || !previous) return;

    setFieldStack(popped);
    trackFieldView(previous.value.id);
    // Reported with the field being left as `previousTitle`, matching legacy, which read whatever
    // sat on top of its prev-field stack at the moment the event fired.
    trackFieldProgress(previous, activeField);
    emitTraverse("back", previous, popped);
  }, [
    activeField,
    fieldStack,
    subFieldIds,
    fieldsMap,
    setFieldStack,
    trackFieldView,
    trackFieldProgress,
    emitTraverse,
  ]);

  const goToField = useCallback(
    (fieldId: string) => {
      const targetIndex = fieldStack.findIndex((node) => node.value.id === fieldId);
      const target = fieldStack[targetIndex];
      if (!target || targetIndex === fieldStack.length - 1) return;

      const newStack = fieldStack.slice(0, targetIndex + 1);
      setFieldStack(newStack);
      trackFieldView(target.value.id);
      trackFieldProgress(target, activeField);
      emitTraverse("jump", target, newStack);
    },
    [fieldStack, activeField, setFieldStack, trackFieldView, trackFieldProgress, emitTraverse]
  );

  const isPreviousDisabled = pageIndex === 0 || !!activeField?.value.options?.disableGoBack;

  const showSubmit = !!activeField && !nextField;

  const getNumberOfRemainingPages = useCallback(() => {
    if (!activeField) return 0;
    return countRemainingFormPages(activeField.value, fieldsArr ?? [], subFieldIds);
  }, [activeField, fieldsArr, subFieldIds]);

  return {
    fieldStack,
    activeField,
    hasNoRootField,
    pageIndex,
    totalPages,
    progress,
    goToNextFieldInternal,
    goToPreviousField,
    goToField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  };
};
